import React, { useState } from "react";

// Link semantics: Source -> Target means Target depends on Source (Target is flagged when Source is approved).
const DEPENDENTS = "dependents";       // sections that depend on this one (this = Source)
const DEPENDENCIES = "dependencies";   // sections this one depends on (this = Target)

const sectionLabel = (s, numbers) => `${numbers?.[s.Id] ? numbers[s.Id] + " " : ""}${s.Title || "Untitled section"}`;

// Source -> [targets] adjacency from a list of { source, target } edges.
const buildGraph = (edges) => {
    const graph = {};
    edges.forEach(({ source, target }) => { (graph[source] = graph[source] || []).push(target); });
    return graph;
};

const reaches = (graph, from, to) => {
    const seen = new Set();
    const stack = [from];
    while (stack.length) {
        const cur = stack.pop();
        if (cur === to) return true;
        if (seen.has(cur)) continue;
        seen.add(cur);
        (graph[cur] || []).forEach((n) => stack.push(n));
    }
    return false;
};

// Section hierarchy (ParentId / Order) as nested nodes.
const buildTree = (sections) => {
    const byParent = {};
    sections.forEach((s) => { const k = s.ParentId ?? "root"; (byParent[k] = byParent[k] || []).push(s); });
    const walk = (key) => (byParent[key] || [])
        .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
        .map((s) => ({ section: s, children: walk(s.Id) }));
    return walk("root");
};

const DirectionSelect = ({ value, onChange, title }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="td-dep-select">
        <option value={DEPENDENTS}>Sections that depend on {title}</option>
        <option value={DEPENDENCIES}>Sections on which {title} depends</option>
    </select>
);

const Modal = ({ title, children, footer, onClose }) => (
    <div className="td-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="td-modal wide">
            <div className="td-modal-h">
                <h3>{title}</h3>
                <span className="td-close-x" onClick={onClose}><i className="fas fa-times" /></span>
            </div>
            <div className="td-modal-b">{children}</div>
            <div className="td-modal-f">{footer}</div>
        </div>
    </div>
);


export const AddDependenciesModal = ({ section, allSections, numbers, sectionLinks, onAddLink, onRemoveLink, onClose }) => {
    const me = section.SectionIdentifier;
    const title = section.Title || "this section";
    const [direction, setDirection] = useState(DEPENDENTS);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const linkedOther = (l, dir) => dir === DEPENDENTS
        ? (l.SourceSectionIdentifier === me ? l.TargetSectionIdentifier : null)
        : (l.TargetSectionIdentifier === me ? l.SourceSectionIdentifier : null);
    const initial = (dir) => new Set(sectionLinks.map((l) => linkedOther(l, dir)).filter(Boolean));

    // Pending selection per direction, so switching the dropdown keeps what was ticked on the other side.
    const [checked, setChecked] = useState(() => ({ [DEPENDENTS]: initial(DEPENDENTS), [DEPENDENCIES]: initial(DEPENDENCIES) }));

    const edgeOf = (dir, other) => dir === DEPENDENTS ? { source: me, target: other } : { source: other, target: me };

    // Links as they would be after saving: other sections' links untouched, this section's replaced by the ticks.
    const pendingEdges = [
        ...sectionLinks
            .filter((l) => l.SourceSectionIdentifier !== me && l.TargetSectionIdentifier !== me)
            .map((l) => ({ source: l.SourceSectionIdentifier, target: l.TargetSectionIdentifier })),
        ...[...checked[DEPENDENTS]].map((o) => edgeOf(DEPENDENTS, o)),
        ...[...checked[DEPENDENCIES]].map((o) => edgeOf(DEPENDENCIES, o)),
    ];
    const graph = buildGraph(pendingEdges);

    const createsLoop = (other) => {
        const { source, target } = edgeOf(direction, other);
        return reaches(graph, target, source);
    };

    const toggle = (other) => {
        const next = new Set(checked[direction]);
        next.has(other) ? next.delete(other) : next.add(other);
        setChecked({ ...checked, [direction]: next });
    };

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            for (const dir of [DEPENDENTS, DEPENDENCIES]) {
                const before = initial(dir);
                for (const l of sectionLinks.filter((l) => { const o = linkedOther(l, dir); return o && !checked[dir].has(o); }))
                    await onRemoveLink(l.Id);
                for (const o of [...checked[dir]].filter((o) => !before.has(o))) {
                    const { source, target } = edgeOf(dir, o);
                    await onAddLink(source, target, "");
                }
            }
            onClose();
        } catch {
            setError("Could not save all dependencies. Some may have been saved; reopen to check.");
        } finally {
            setBusy(false);
        }
    };

    const renderNode = ({ section: s, children }, depth) => {
        const id = s.SectionIdentifier;
        const isSelf = id === me;
        const isChecked = checked[direction].has(id);
        const loop = !isSelf && !isChecked && createsLoop(id);
        const disabled = isSelf || loop;
        return (
            <React.Fragment key={s.Id}>
                <label
                    className={`td-dep-row ${disabled ? "disabled" : ""}`}
                    style={{ paddingLeft: 8 + depth * 18 }}
                    title={isSelf ? "This section" : loop ? "Would create a dependency loop" : ""}
                >
                    <input type="checkbox" checked={isChecked} disabled={disabled} onChange={() => toggle(id)} />
                    <span className="num">{numbers?.[s.Id]}</span>
                    <span className="ttl">{s.Title || "Untitled section"}</span>
                    {isSelf && <span className="td-muted" style={{ fontSize: 11 }}>(this section)</span>}
                    {loop && <i className="fas fa-sync-alt td-muted" style={{ fontSize: 10 }} />}
                </label>
                {children.map((c) => renderNode(c, depth + 1))}
            </React.Fragment>
        );
    };

    return (
        <Modal
            title={<><i className="fas fa-project-diagram" /> Add dependencies</>}
            onClose={onClose}
            footer={<>
                <span className="caption">Sections that would create a loop can't be selected.</span>
                <div style={{ display: "flex", gap: 10 }}>
                    <button className="td-btn td-btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="td-btn td-btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
                </div>
            </>}
        >
            <DirectionSelect value={direction} onChange={setDirection} title={title} />
            <div className="td-dep-tree">{buildTree(allSections).map((n) => renderNode(n, 0))}</div>
            {error && <div className="td-err">{error}</div>}
        </Modal>
    );
};


export const SeeDependenciesModal = ({ section, allSections, numbers, sectionLinks, onClose }) => {
    const title = section.Title || "this section";
    const [direction, setDirection] = useState(DEPENDENTS);
    const byId = {};
    allSections.forEach((s) => { byId[s.SectionIdentifier] = s; });

    const next = (id) => sectionLinks
        .filter((l) => direction === DEPENDENTS ? l.SourceSectionIdentifier === id : l.TargetSectionIdentifier === id)
        .map((l) => ({ id: direction === DEPENDENTS ? l.TargetSectionIdentifier : l.SourceSectionIdentifier, note: l.Note }))
        .filter((n) => byId[n.id]);

    // Path set guards against loops left over from before loops were blocked.
    const renderBranch = (id, depth, path) => next(id).map((n) => (
        <React.Fragment key={`${path.join("/")}/${n.id}`}>
            <div className="td-dep-row" style={{ paddingLeft: 8 + depth * 18 }} title={n.note || ""}>
                <i className={`fas ${direction === DEPENDENTS ? "fa-level-up-alt fa-rotate-90" : "fa-level-down-alt fa-rotate-90"} td-muted`} style={{ fontSize: 10 }} />
                <span className="num">{numbers?.[byId[n.id].Id]}</span>
                <span className="ttl">{byId[n.id].Title || "Untitled section"}</span>
            </div>
            {!path.includes(n.id) && renderBranch(n.id, depth + 1, [...path, n.id])}
        </React.Fragment>
    ));

    const branches = renderBranch(section.SectionIdentifier, 1, [section.SectionIdentifier]);

    return (
        <Modal
            title={<><i className="fas fa-sitemap" /> Dependencies</>}
            onClose={onClose}
            footer={<><span /><button className="td-btn td-btn-primary" onClick={onClose}>Close</button></>}
        >
            <DirectionSelect value={direction} onChange={setDirection} title={title} />
            <div className="td-dep-tree">
                <div className="td-dep-row root">
                    <span className="num">{numbers?.[section.Id]}</span>
                    <span className="ttl">{sectionLabel(section, null)}</span>
                </div>
                {branches.length ? branches : (
                    <div className="td-muted" style={{ fontSize: 12, padding: "8px 26px" }}>
                        {direction === DEPENDENTS ? "No sections depend on this one." : "This section doesn't depend on any section."}
                    </div>
                )}
            </div>
        </Modal>
    );
};
